import { motion } from 'framer-motion';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import PeopleCommunitySection from '../home/components/PeopleCommunitySection';
import { useTranslation } from 'react-i18next';

const AboutPage = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section with Team Photo */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden pt-20">
        <div className="absolute inset-0">
          <img
            src="https://static.readdy.ai/image/502796c101e72168e087fe40585d8901/b189c2acc18831f3347827e3ed99f207.jpeg"
            alt="Yoshino Village Network メンバー集合写真"
            className="w-full h-full object-cover object-center md:object-[center_60%]"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-7xl font-black text-white mb-8 leading-tight">
              {t('aboutPage.hero.title')}
            </h1>
            
            <p className="text-2xl md:text-3xl text-white/95 mb-6 font-bold">
              {t('aboutPage.hero.subtitle')}
            </p>

            <p className="text-lg md:text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
              {t('aboutPage.hero.description')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Yoshino Village Networkとは */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-12 text-center">
              {t('aboutPage.whatIsYVN.title')}
            </h2>
            <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-3xl p-10 md:p-14 shadow-lg">
              <p className="text-xl text-gray-700 leading-relaxed mb-6">
                {t('aboutPage.whatIsYVN.description1')}
              </p>
              <p className="text-xl text-gray-700 leading-relaxed mb-6">
                {t('aboutPage.whatIsYVN.description2')}
              </p>
              <p className="text-xl text-gray-900 font-bold">
                {t('aboutPage.whatIsYVN.description3')}
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* なぜ始めたのか */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-cyan-50">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-12 text-center">
              {t('aboutPage.whyStarted.title')}
            </h2>
            <div className="bg-white rounded-3xl p-10 md:p-12 shadow-lg mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                {t('aboutPage.whyStarted.challenges.title')}
              </h3>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                {t('aboutPage.whyStarted.challenges.description1')}
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                {t('aboutPage.whyStarted.challenges.description2')}
              </p>
            </div>
            <div className="bg-white rounded-3xl p-10 md:p-12 shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                {t('aboutPage.whyStarted.answer.title')}
              </h3>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                {t('aboutPage.whyStarted.answer.description1')}
              </p>
              <p className="text-lg text-gray-900 font-bold mb-4">
                {t('aboutPage.whyStarted.answer.description2')}
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                {t('aboutPage.whyStarted.answer.description3')}
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 私たちの活動 */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-12 text-center">
              {t('aboutPage.activities.title')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-3xl p-8 shadow-lg">
                <div className="w-16 h-16 flex items-center justify-center bg-orange-600 rounded-2xl mb-6">
                  <i className="ri-door-open-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {t('aboutPage.activities.gateway.title')}
                </h3>
                <p className="text-base text-gray-700 leading-relaxed">
                  {t('aboutPage.activities.gateway.description')}
                </p>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-3xl p-8 shadow-lg">
                <div className="w-16 h-16 flex items-center justify-center bg-green-600 rounded-2xl mb-6">
                  <i className="ri-lightbulb-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {t('aboutPage.activities.jobs.title')}
                </h3>
                <p className="text-base text-gray-700 leading-relaxed">
                  {t('aboutPage.activities.jobs.description')}
                </p>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-3xl p-8 shadow-lg">
                <div className="w-16 h-16 flex items-center justify-center bg-blue-600 rounded-2xl mb-6">
                  <i className="ri-team-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {t('aboutPage.activities.community.title')}
                </h3>
                <p className="text-base text-gray-700 leading-relaxed">
                  {t('aboutPage.activities.community.description')}
                </p>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-3xl p-8 shadow-lg">
                <div className="w-16 h-16 flex items-center justify-center bg-purple-600 rounded-2xl mb-6">
                  <i className="ri-share-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {t('aboutPage.activities.information.title')}
                </h3>
                <p className="text-base text-gray-700 leading-relaxed">
                  {t('aboutPage.activities.information.description')}
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 私たちの価値観 */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-12 text-center">
              {t('aboutPage.values.title')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-20 h-20 flex items-center justify-center bg-orange-100 rounded-2xl mx-auto mb-6">
                  <i className="ri-flask-line text-4xl text-orange-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {t('aboutPage.values.experimental.title')}
                </h3>
                <p className="text-base text-gray-600 leading-relaxed">
                  {t('aboutPage.values.experimental.description')}
                </p>
              </div>

              <div className="text-center">
                <div className="w-20 h-20 flex items-center justify-center bg-orange-100 rounded-2xl mx-auto mb-6">
                  <i className="ri-tools-line text-4xl text-orange-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {t('aboutPage.values.workInProgress.title')}
                </h3>
                <p className="text-base text-gray-600 leading-relaxed">
                  {t('aboutPage.values.workInProgress.description')}
                </p>
              </div>

              <div className="text-center">
                <div className="w-20 h-20 flex items-center justify-center bg-orange-100 rounded-2xl mx-auto mb-6">
                  <i className="ri-team-line text-4xl text-orange-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {t('aboutPage.values.equal.title')}
                </h3>
                <p className="text-base text-gray-600 leading-relaxed">
                  {t('aboutPage.values.equal.description')}
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* メンバー紹介 - ホームページと同じコンポーネントを使用 */}
      <PeopleCommunitySection />

      {/* 私たちが目指すもの */}
      <section className="py-20 bg-gradient-to-br from-orange-600 to-red-600 text-white">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-black mb-12 text-center">
              {t('aboutPage.vision.title')}
            </h2>
            <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-10 md:p-14">
              <div className="space-y-8">
                <div>
                  <h3 className="text-2xl font-bold mb-4">
                    {t('aboutPage.vision.settlement.title')}
                  </h3>
                  <p className="text-lg leading-relaxed opacity-95">
                    {t('aboutPage.vision.settlement.description')}
                  </p>
                </div>

                <div>
                  <h3 className="text-2xl font-bold mb-4">
                    {t('aboutPage.vision.pride.title')}
                  </h3>
                  <p className="text-lg leading-relaxed opacity-95">
                    {t('aboutPage.vision.pride.description')}
                  </p>
                </div>

                <div>
                  <h3 className="text-2xl font-bold mb-4">
                    {t('aboutPage.vision.diverse.title')}
                  </h3>
                  <p className="text-lg leading-relaxed opacity-95">
                    {t('aboutPage.vision.diverse.description')}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTAセクション */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">
              {t('aboutPage.cta.title')}
            </h2>
            <p className="text-xl text-gray-600 mb-12 leading-relaxed">
              {t('aboutPage.cta.description')}
            </p>
            <a
              href="/consultation"
              className="inline-flex items-center gap-3 bg-orange-600 hover:bg-orange-700 text-white px-10 py-5 rounded-lg text-lg font-bold transition-all shadow-xl hover:shadow-2xl hover:-translate-y-1 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-chat-3-line text-xl"></i>
              {t('aboutPage.cta.button')}
            </a>
            <p className="text-sm text-gray-500 mt-6">
              {t('aboutPage.cta.responseTime')}
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
